import { WorkItem, WorkItemLink } from "WorkItemTracking/Scripts/TFS.WorkItemTracking";
import { IWorkItemMention } from "Mention/Scripts/WorkItem/WorkItemMentionModels";
import { TfsContext } from "Presentation/Scripts/TFS/TFS.Host.TfsContext";
import { PerfScenarioManager } from "WorkItemTracking/Scripts/Utils/PerfScenarioManager";
import * as Utils_String from "VSS/Utils/String";
import * as WorkItemTrackingResources from "WorkItemTracking/Scripts/Resources/TFS.Resources.WorkItemTracking";
import * as CIConstants from "WorkItemTracking/Scripts/CustomerIntelligence";
import * as Diag from "VSS/Diag";
import * as WITConstants from "Presentation/Scripts/TFS/Generated/TFS.WorkItemTracking.Constants";
import { FeatureAvailabilityService } from "VSS/FeatureAvailability/Services";
import { FeatureAvailabilityFlags } from "Presentation/Scripts/TFS/Generated/TFS.Server.WebAccess.Constants";
import * as MentionsWorkItems from "Mention/Scripts/TFS.Mention.WorkItems";
import { getWorkItemMentionText } from "Mention/Scripts/WorkItem/WorkItemMentionUtilities";
import { Constants } from "Mention/Scripts/TFS.Mention";

const RelatedLinkTypeEndName: string = "Related";

/**
 * Goes through pending html field changes for workItem and adds a workItemLink per corresponding #mention.
 * @param workItem WorkItem to process.
 */
export function parseMentionsFromHtmlFields(workItem: WorkItem, historyOnly: boolean = false) {
    try {
        Object.keys(workItem.fieldUpdates).forEach((fieldId) => {
            const field = workItem.fieldMapById[fieldId];
            const text: string = field.getValue();
            
            // Confirm that the text has a mention in it for performance
            if (typeof text === "string" && (text.indexOf(Constants.HTML_MENTION_ATTR_NAME) !== -1)) {

                // In case of html field mentions, we want to strip out the type and title from work item mentions just before saving,
                // so that they can see the type and title in the rich text box, but those don't get persisted.
                if ((field.fieldDefinition.type === WITConstants.FieldType.History) || ((field.fieldDefinition.type === WITConstants.FieldType.Html) && !historyOnly)) {
                    const sanitizedText = removeTypeAndTitleFromWorkItemMentions(text);
                    
                    if (text !== sanitizedText) {
                        field.setValue(sanitizedText, true);
                    }

                    if (workItem.store.linkTypes && workItem.store.linkTypes.length > 0) {
                        const scenario = PerfScenarioManager.startScenario(
                            CIConstants.WITPerformanceScenario.WORKITEM_RELATED_LINKING,
                            false
                        );
            
                        if (text) {
                            const $html = $("<p>" + text + "</p>");
                            MentionsWorkItems.WorkItemsMentionParser
                                .getDefault()
                                .parseFromHtml($html, ($a, mention) => {
                                    if (mention) {
                                        _processMention(workItem, mention, field.fieldDefinition.type);
                                    }
                                });
                            scenario.addSplitTiming(CIConstants.PerformanceEvents.WORKITEMRELATED_LINKING_COMPLETE);
                            PerfScenarioManager.endScenario(
                                CIConstants.WITPerformanceScenario.WORKITEM_RELATED_LINKING);
                        }
                    }
                }
            }
        });
    } catch (ex) {
        Diag.logError(ex);
    }
}

/**
 * Replaces the work item type and title in work item mentions with #id, to avoid saving those in the DB
 * Public for unit testing.
 * @param value work item history field value.
 */
export function removeTypeAndTitleFromWorkItemMentions(value: string): string {
    if (!_shouldHideTypeAndTitleInWorkItemMentions()) {
        return value;
    }

    const $html = $("<span>" + value + "</span>");
    MentionsWorkItems.WorkItemsMentionParser.getDefault().parseFromHtml($html, ($a, mention) => {
        if (mention) {
            const mentionText = getWorkItemMentionText(mention.workItemId);
            if ($a.text() !== mentionText) {
                $a.text(mentionText);
            }
        }
    });
    return $html.html();
}

function _processMention(workItem: WorkItem, mention: IWorkItemMention, fieldType: WITConstants.FieldType) {
    if (!mention) {
        return;
    }

    if (workItem.id === mention.workItemId) {
        return;
    }

    const existingLinks = workItem.getLinks();
    if (!existingLinks) {
        return;
    }

    const sameLinkExists = existingLinks.some((link, index, array) => {

        if (!(link instanceof WorkItemLink)) {
            return false;
        }

        const workItemLink = <WorkItemLink>link;
        const linkTypeEnd = workItemLink.getLinkTypeEnd();
        if (!linkTypeEnd) {
            return false;
        }

        const match = (linkTypeEnd.name.toLowerCase() === RelatedLinkTypeEndName.toLowerCase() &&
            workItemLink.getTargetId() === mention.workItemId);
        return match;
    });

    if (sameLinkExists) {
        return;
    }

    const changedBy = TfsContext.getDefault().currentIdentity.displayName;
    const comment = Utils_String.format(fieldType === WITConstants.FieldType.History ? WorkItemTrackingResources.WorkItemDiscussionMentionLinkComment : WorkItemTrackingResources.WorkItemHtmlFieldMentionLinkComment,
        changedBy,
        mention.workItemId,
        workItem.id);

    const workItemLink = WorkItemLink.create(workItem, RelatedLinkTypeEndName, mention.workItemId, comment);
    workItem.addLink(workItemLink);
}

function _shouldHideTypeAndTitleInWorkItemMentions(): boolean {
    return FeatureAvailabilityService.isFeatureEnabled(FeatureAvailabilityFlags.HideTypeAndTitleInWorkItemMentions, false);
}
